// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import v4 from 'uuid/v4';

import { basketOutcomeSelected, basketOutcomeUnselected } from '../../actions/basket';
import { useBetGroupFactoryContext } from '../../contexts/BetGroupFactoryContext';
import { useMatchHotBetGroupOutcomeContext } from '../../contexts/MatchHotBetGroupOutcomeContext';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import useMergeProps from '../../hooks/useMergeProps';
import useShallowEqualSelector from '../../hooks/useShallowEqualSelector';
import type { BetTemplate } from '../../types/bet';
import { SportIds } from '../../types/sports';

import BetGroupOutcome, { type BetGroupOutcomePropsType } from './BetGroupOutcome';
import makeBetGroupOutcomePropsSelector from './BetGroupOutcome.selector';

type PropsType = {
  oddId: number;
  matchId: number;
  matchIsAvailable: boolean;
  betIsAvailable: boolean;
  betId: number;
  betTemplate: BetTemplate;
  showPercentDistribution: boolean;
  isFromLineUp?: boolean;
  displayLabel?: boolean;
  isFromMultichoice?: boolean;
  noLabel?: boolean;
  className?: string;
};

const BIGGER_ICONS = [SportIds.FORMULA_1, SportIds.CYCLING, SportIds.MOTO];

const BetGroupOutcomeContainer = (ownProps: PropsType) => {
  const {
    oddId: oddIdFromProps,
    matchId: matchIdFromProps,
    betId: betIdFromProps,
    isFromLineUp,
    isFromMultichoice: isFromMultichoiceFromProps,
    showPercentDistribution,
    noLabel,
  } = ownProps;
  const { isMultichoice: isFromMultichoiceFromBetGroupFactoryContext } = useBetGroupFactoryContext();
  const {
    outcomeId: outcomeIdFromMatchHotBetGroupOutcomeContext,
    betId: betIdFromMatchHotBetGroupOutcomeContext,
    isMultichoice: isFromMultichoiceFromMatchHotBetGroupOutcomeContext,
  } = useMatchHotBetGroupOutcomeContext();
  const { matchId: matchIdFromMatchViewContext } = useMatchViewContext();

  const location = useLocation();
  const matchId = useMemo(
    () => matchIdFromProps ?? matchIdFromMatchViewContext,
    [matchIdFromProps, matchIdFromMatchViewContext],
  );
  const oddId = useMemo(
    () => oddIdFromProps ?? outcomeIdFromMatchHotBetGroupOutcomeContext,
    [oddIdFromProps, outcomeIdFromMatchHotBetGroupOutcomeContext],
  );
  const betId = useMemo(
    () => betIdFromProps ?? betIdFromMatchHotBetGroupOutcomeContext,
    [betIdFromProps, betIdFromMatchHotBetGroupOutcomeContext],
  );

  const isFromMultichoice = useMemo(
    () =>
      isFromMultichoiceFromProps ??
      isFromMultichoiceFromBetGroupFactoryContext ??
      isFromMultichoiceFromMatchHotBetGroupOutcomeContext,
    [
      isFromMultichoiceFromBetGroupFactoryContext,
      isFromMultichoiceFromProps,
      isFromMultichoiceFromMatchHotBetGroupOutcomeContext,
    ],
  );

  const dispatch = useDispatch();

  const selector = useMemo(() => makeBetGroupOutcomePropsSelector(), []);
  const selectorProps = useMemo(
    () => ({
      ...ownProps,
      isFromMultichoice,
      pathname: location.pathname,
      showPercentDistribution,
      oddId,
      betId,
      matchId,
      noLabel,
    }),
    [ownProps, isFromMultichoice, location, showPercentDistribution, oddId, betId, matchId, noLabel],
  );
  const selectedProps = useShallowEqualSelector((state) => selector(state, selectorProps));

  const uuid = useMemo(() => v4(), []);

  const addItem = useCallback(() => {
    dispatch(
      basketOutcomeSelected({
        oddId,
        matchId,
        betId,
        sourceElementUuid: uuid,
        isFromLineUp,
        isFromMultichoice,
      }),
    );
  }, [dispatch, oddId, matchId, betId, uuid, isFromLineUp, isFromMultichoice]);

  const delItem = useCallback(() => {
    dispatch(basketOutcomeUnselected({ oddId, matchId, isFromMultichoice }));
  }, [oddId, matchId, dispatch, isFromMultichoice]);

  const otherProps = useMemo(
    () => ({
      anchorId: oddId ? `match-group-${matchId}-${oddId}` : undefined,
      uuid,
      addItem,
      delItem,
      isFromLineUp,
      isBiggerFlag: BIGGER_ICONS.indexOf(selectedProps.sportId) > -1,
      oddId,
    }),
    [oddId, matchId, uuid, addItem, delItem, isFromLineUp, selectedProps.sportId],
  );

  const enhancedProps: BetGroupOutcomePropsType = useMergeProps({ ownProps, selectedProps, otherProps });

  return <BetGroupOutcome {...enhancedProps} key={oddId} />;
};

export default BetGroupOutcomeContainer;
